import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  AppCard,
  AppDeleteModal,
  AppFileUploader,
  AppInput,
  AppModal,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from '@/layout/AppComponents';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import styled from 'styled-components';
import LazyLoad from 'react-lazyload';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../layout/components/helpers/Translator';
import { axiosPath } from '../../../../routing/helpers/router';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import CommunicationPipelineAdminNavbar from '../../../../layout/screenSpecifics/Modules/CommunicationPipeline/Admin/CommunicationPipelineAdminNavbar';
import { statusSteps } from '../../../../layout/screenSpecifics/Modules/CommunicationPipeline/constants/options';
import AppButton from '../../../../layout/components/form/AppButton';
import minions from '../../../../minions/minions';
import CommunicationPipelineProgressTimeLine from '../../../../layout/screenSpecifics/Modules/CommunicationPipeline/CommunicationPipelineProgressTimeLine';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import CommunicationPipelineModuleSidebar from '@/layout/screenSpecifics/Modules/CommunicationPipeline/CommunicationPipelineModuleSidebar';
import * as currentPageAction from '@/store/actions/currentPageAction';

export default function CommunicationPipelineAdminReportAllegationsScreen() {
  const { moduleBridgeHex, reportHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [step] = useState('allegations');
  const [report, setReport] = useState();
  const [isOpened, setIsOpened] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteHex, setDeleteHex] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />),
      ),
    }),
  );

  const loadReport = useCallback(async () => {
    axios
      .post(axiosPath('api_r_user_module_communicationPipeline_report_get'), {
        moduleBridgeHex,
        reportHex,
        step,
      })
      .then((r) => {
        if (isMounted.current) {
          setReport(r?.data);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, reportHex, step]);

  useEffect(() => {
    loadReport();
  }, []);

  const handleIsOpened = useCallback(
    (evidenceHex) => {
      if (!isOpened.includes(evidenceHex)) {
        setIsOpened([...isOpened, evidenceHex]);
      } else {
        setIsOpened(isOpened.filter((hex) => hex !== evidenceHex));
      }
    },
    [isOpened],
  );

  const handleAdd = useCallback(
    async (payload) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_communicationPipeline_admin_report_evidence_add',
          ),
          minions.forms.toFormData({
            ...payload,
            moduleBridgeHex,
            reportHex,
            step,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then((r) => {
          if (isMounted.current && r?.data?.evidence) {
            const updatedEvidences = report?.evidences ?? [];
            updatedEvidences.push(r?.data?.evidence);
            setReport({ ...report, evidences: updatedEvidences });
            setShowAddModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, reportHex, report, step],
  );

  const handleDelete = useCallback(async () => {
    const pageStepIndex = statusSteps.findIndex((ss) => ss.value === step);
    const currentStepIndex = statusSteps.findIndex(
      (ss) => ss.value === report?.status.step,
    );

    if (
      currentStepIndex > pageStepIndex ||
      report?.status?.progress === 'completed'
    ) {
      AppNotificationAlert({
        title: ReactDOMServer.renderToString(
          <Translator file="module-communicationPipeline" name="notice" />,
        ),
        message: ReactDOMServer.renderToString(
          <Translator
            file="module-communicationPipeline"
            name="information-cannot-modified-state-marked-completed"
          />,
        ),
        type: 'warning',
        duration: 5000,
        position: 'top-right',
      });
      setDeleteHex(undefined);
      return;
    }
    axios
      .delete(
        axiosPath(
          'api_r_user_module_communicationPipeline_admin_report_evidence_delete',
        ),
        { data: { moduleBridgeHex, evidenceHex: deleteHex } },
      )
      .then(() => {
        if (isMounted.current) {
          const updatedEvidences = report?.evidences.filter(
            (e) => e?.hex !== deleteHex,
          );
          setReport({ ...report, evidences: updatedEvidences });
          setDeleteHex(undefined);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, reportHex, step, report, deleteHex]);

  // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(
        axiosPath('api_r_user_module_communicationPipeline_getModuleData'),
        { moduleBridgeHex },
      )
      .then((r) => {
        dispatch(currentPageAction.change('moduleData', r.data, true));
      });
  }, []);
  // > LOAD MODULE DATA //

  const handleGetFile = useCallback(
    async (evidence, salt) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_communicationPipeline_admin_report_evidence_file_get',
          ),
          {
            moduleBridgeHex,
            reportHex,
            evidenceHex: evidence.hex,
            salt,
          },
        )
        .then(({ data }) => {
          window.open(data, '_blank', 'noopener,noreferrer');
        })
        .catch((e) => errorHandler(e));
    },
    [moduleBridgeHex, reportHex],
  );

  if (!moduleData) return <AppNoInfo />;

  return (
    <>
      {moduleData?.permissionLevel === 0 && (
        <CommunicationPipelineModuleSidebar />
      )}
      <Container>
        <BreadcrumbHandler
          paths={
            moduleBridgeHex
              ? [
                  {
                    name: `${moduleData?.moduleName}`,
                    route: {
                      name: 'user_module_communicationPipeline_home',
                      params: {
                        moduleBridgeHex,
                      },
                    },
                  },
                  {
                    name: `${ReactDOMServer.renderToString(
                      <Translator
                        file="module-communicationPipeline"
                        name="complaint"
                      />,
                    )} - ${reportHex}`,
                  },
                ]
              : [{ name: '...' }]
          }
        />
        <CommunicationPipelineAdminNavbar />
        <h3 className="mb-3">{statusSteps[4]?.label}</h3>
        {report && (
          <>
            <AppCard className="mb-3">
              <CommunicationPipelineProgressTimeLine
                report={report}
                pageStep={step}
              />
            </AppCard>
            <AppButton
              color="success"
              className="float-right mb-2"
              icon="add"
              size="xs"
              onClick={() => setShowAddModal(true)}
            >
              <Translator
                file="module-communicationPipeline"
                name="add-information"
              />
            </AppButton>
            <div className="clearfix" />
            <AppDeleteModal
              show={!!deleteHex}
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-communicationPipeline"
                  name="delete-document"
                />,
              )}
              onClose={() => setDeleteHex(undefined)}
              onConfirm={handleDelete}
              sensitive
              sensitiveWord={ReactDOMServer.renderToString(
                <Translator
                  file="module-communicationPipeline"
                  name="delete"
                />,
              )}
            />
            <AppModal
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-communicationPipeline"
                  name="add-information"
                />,
              )}
              show={showAddModal}
              onClose={() => setShowAddModal(false)}
            >
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={{
                  name: '',
                  description: '',
                  files: [],
                }}
                onSubmit={handleAdd}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <AppInput
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="name"
                        />,
                      )}
                      value={values?.name}
                      error={errors?.name}
                      onChange={(v) => setFieldValue('name', v)}
                    />
                    <AppTextArea
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="description"
                        />,
                      )}
                      value={values?.description}
                      onChange={(v) => setFieldValue('description', v)}
                      resize="vertical"
                      height="180px"
                    />
                    <AppFileUploader
                      onChange={(v) => setFieldValue('files', v)}
                      value={values.files}
                      placeholder={
                        values?.files?.length === 0 ? (
                          <Translator
                            file="module-communicationPipeline"
                            name="select-one-more-files"
                          />
                        ) : (
                          <Translator
                            file="module-communicationPipeline"
                            name="elements-selected-elements"
                            variables={{ elements: values?.files.length }}
                          />
                        )
                      }
                      isMulti
                    />
                    <button
                      type="submit"
                      className="btn btn-success btn-sm float-right"
                      disabled={isSubmitting}
                    >
                      <Translator
                        file="module-communicationPipeline"
                        name="add"
                      />
                    </button>
                  </form>
                )}
              </Formik>
            </AppModal>
            {report?.evidences.map((e) => (
              <LazyLoad key={e?.hex}>
                <AppCard className="mb-2">
                  <div
                    className="d-flex align-items-center pointer"
                    onClick={() => handleIsOpened(e?.hex)}
                  >
                    <span className="mr-3">
                      <AppRemixIcon
                        icon={isOpened.includes(e?.hex) ? 'subtract' : 'add'}
                        size={20}
                      />
                    </span>
                    <span>
                      <small>{e?.dateStamp}</small> - {e?.name} ({e?.hex})
                    </span>
                  </div>
                  <UnmountClosed isOpened={isOpened.includes(e?.hex)}>
                    <span
                      className="btn btn-danger btn-xs float-right"
                      onClick={() => {
                        setDeleteHex(e?.hex);
                      }}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-communicationPipeline"
                          name="delete"
                        />,
                      )}
                    >
                      <AppRemixIcon icon="delete-bin-6" />
                    </span>
                    <div className="clearfix" />
                    <h3 className="mt-0">
                      <Translator
                        file="module-communicationPipeline"
                        name="name"
                      />
                    </h3>
                    <AppInput value={e?.name} isReadOnly />
                    <h3>
                      <Translator
                        file="module-communicationPipeline"
                        name="description"
                      />
                    </h3>
                    <AppTextArea
                      value={e?.description}
                      resize="vertical"
                      height="180px"
                      isReadOnly
                    />
                    {e?.files && Object.keys(e?.files).length > 0 && (
                      <>
                        <h3>
                          <Translator
                            file="module-communicationPipeline"
                            name="documents"
                          />
                        </h3>
                        <div className="table-responsive">
                          <table className="table table-hover table-sm">
                            <tbody>
                              {Object.keys(e?.files).map((salt) => (
                                <tr key={salt}>
                                  <td>
                                    <span
                                      onClick={() => handleGetFile(e,salt)}
                                      // href={`${global.urlData.userApiUrl}m/communication-pipeline/report/evidence/get-file/${moduleBridgeHex}/${reportHex}/${e?.hex}/${salt}`}
                                      title={e?.files?.[salt]?.name}
                                      className="pointer"
                                    >
                                      <AppRemixIcon icon="file-text" />{' '}
                                      {e?.files?.[salt]?.name}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </UnmountClosed>
                </AppCard>
              </LazyLoad>
            ))}
            {report?.evidences.length === 0 && (
              <div className="alert alert-info">
                <Translator
                  file="module-communicationPipeline"
                  name="no-information-exists"
                />
              </div>
            )}
          </>
        )}
      </Container>
    </>
  );
}

const Container = styled.div`
  padding-left: 0.5rem !important;
  padding-bottom: 100px;
  h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 32px 0 0 0;
  }
  .minHeight600 {
    min-height: 600px;
  }
  .user-info {
    border: 1px solid lightgray;
    padding: 24px;
  }
`;
